import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useEnvStore } from "@/os/env/useEnvStore";

/**
 * Global data filters that affect what gets surfaced across the whole OS
 * (Dashboard, Clientes, Proyectos, Cotizaciones, Facturas).
 *
 * - `activeYear`: when set to a year, lists/aggregations only include rows
 *   whose activity falls in that year. `"all"` disables the filter.
 * - `showSimulated`: when false, every client flagged as `is_simulated` and
 *   all of their projects/quotes/invoices/payments are hidden everywhere
 *   (KPIs included). When true, they appear as if they were real clients.
 *   Defaults to `true` in sandbox, `false` in production.
 */
export type DataFilter = {
  activeYear: number | "all";
  showSimulated: boolean;
  /** When false, clients with status="inactive" are hidden across the OS (and don't count in KPIs). */
  showInactiveClients: boolean;
  /** When false, projects with status="archived" are hidden across the OS. */
  showArchivedProjects: boolean;
  /** When false, quotes with status="expired" are hidden across the OS. */
  showExpiredQuotes: boolean;
};

type Store = DataFilter & {
  setActiveYear: (y: number | "all") => void;
  setShowSimulated: (v: boolean) => void;
  setShowInactiveClients: (v: boolean) => void;
  setShowArchivedProjects: (v: boolean) => void;
  setShowExpiredQuotes: (v: boolean) => void;
};

const defaultShowSimulated = () =>
  useEnvStore.getState().env === "sandbox";

export const useDataFilterStore = create<Store>()(
  persist(
    (set) => ({
      activeYear: "all",
      showSimulated: defaultShowSimulated(),
      showInactiveClients: false,
      showArchivedProjects: false,
      showExpiredQuotes: false,
      setActiveYear: (activeYear) => set({ activeYear }),
      setShowSimulated: (showSimulated) => set({ showSimulated }),
      setShowInactiveClients: (showInactiveClients) => set({ showInactiveClients }),
      setShowArchivedProjects: (showArchivedProjects) => set({ showArchivedProjects }),
      setShowExpiredQuotes: (showExpiredQuotes) => set({ showExpiredQuotes }),
    }),
    { name: "sc-os-data-filter" },
  ),
);

export const getDataFilter = (): DataFilter => {
  const s = useDataFilterStore.getState();
  return {
    activeYear: s.activeYear,
    showSimulated: s.showSimulated,
    showInactiveClients: s.showInactiveClients,
    showArchivedProjects: s.showArchivedProjects,
    showExpiredQuotes: s.showExpiredQuotes,
  };
};

/** True if the given ISO date falls within `year` (or filter is "all"). */
export function isInYear(iso: string | null | undefined, year: number | "all"): boolean {
  if (year === "all") return true;
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  return d.getUTCFullYear() === year;
}

/** True if any of the given ISO dates falls within `year`. */
export function anyInYear(isos: Array<string | null | undefined>, year: number | "all"): boolean {
  if (year === "all") return true;
  return isos.some((iso) => isInYear(iso, year));
}